// Utility function for making authenticated API calls
export const authenticatedFetch = async (url, options = {}) => {
  const token = localStorage.getItem("authToken");

  if (!token) {
    throw new Error("No authentication token found");
  }

  // Add authorization header and cache control
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    "Cache-Control": "no-cache, no-store, must-revalidate",
    Pragma: "no-cache",
    Expires: "0",
    ...options.headers,
  };

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    // If token is expired, try to refresh it
    if (response.status === 401) {
      const refreshToken = localStorage.getItem("refreshToken");
      if (refreshToken) {
        try {
          const refreshResponse = await fetch(
            "https://pulse.upstartaec.com/agent/auth/refresh",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ refresh_token: refreshToken }),
            }
          );

          if (refreshResponse.ok) {
            const data = await refreshResponse.json();
            localStorage.setItem("authToken", data.data.access_token);
            localStorage.setItem("refreshToken", data.data.refresh_token);

            // Retry the original request with new token
            const newToken = data.data.access_token;
            const newHeaders = {
              "Content-Type": "application/json",
              Authorization: `Bearer ${newToken}`,
              "Cache-Control": "no-cache, no-store, must-revalidate",
              Pragma: "no-cache",
              Expires: "0",
              ...options.headers,
            };

            const retryResponse = await fetch(url, {
              ...options,
              headers: newHeaders,
            });

            if (retryResponse.ok) {
              return await retryResponse.json();
            } else {
              throw new Error(
                `Request failed with status: ${retryResponse.status}`
              );
            }
          } else {
            // Refresh failed, clear tokens
            localStorage.removeItem("authToken");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("userEmail");
            localStorage.removeItem("userId");
            throw new Error("Authentication expired. Please log in again.");
          }
        } catch (error) {
          // Refresh failed, clear tokens
          localStorage.removeItem("authToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("userEmail");
          localStorage.removeItem("userId");
          throw new Error("Authentication expired. Please log in again.");
        }
      } else {
        throw new Error("Authentication expired. Please log in again.");
      }
    }

    if (response.ok || response.status === 304) {
      // Handle 304 Not Modified - return cached data or empty response
      if (response.status === 304) {
        // For 304, we might not have response body, so return a success response
        return { success: true, message: "Data not modified", data: null };
      }
      return await response.json();
    } else {
      throw new Error(`Request failed with status: ${response.status}`);
    }
  } catch (error) {
    throw error;
  }
};

// Utility function to check if token is expired
export const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch (error) {
    return true;
  }
};

// Utility function to get token expiration time
export const getTokenExpirationTime = (token) => {
  if (!token) return null;

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000; // Convert to milliseconds
  } catch (error) {
    return null;
  }
};
